
import React, { useState } from 'react';
import { X, Sparkles, Loader2, CheckCircle, AlertCircle, Droplet, ShieldCheck, Terminal, FileText } from 'lucide-react';
import { extractProjectFromText } from '../services/geminiService';
// Fix: Added Country to type imports
import type { Project, BitumenSpec, ProjectUpdate, Country } from '../types';
import { ProjectStatus } from '../types';

interface ImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImport: (project: Project) => void;
  // Fix: Added activeCountry prop to provide branch context for project creation
  activeCountry: Country;
}

const SAMPLE_TEXTS = [
  {
    label: "Road Brief",
    text: "Minutes: Discussed the Mwanza Inner Ring Road project. China Road & Bridge Corp confirmed they have reached 30% progress on the 55.4km stretch. Financed by AfDB. Supervising consultant is Howard Humphreys. We need 1500 tons of 60/70 grade bitumen by June. Construction officially started on 2024-01-10."
  },
  {
    label: "Email Update",
    text: "RE: Morogoro Bypass. Hanil Construction reports 65% progress. Distance is 42km. Current request for 400t of MC-30 grade bitumen. Estimated handover: 2025-05-15."
  }
];

export const ImportModal: React.FC<ImportModalProps> = ({ isOpen, onClose, onImport, activeCountry }) => {
  const [text, setText] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [extractedData, setExtractedData] = useState<Partial<Project> | null>(null);
  const [error, setError] = useState('');

  const handleAnalyze = async () => {
    if (!text.trim()) return;
    
    setIsAnalyzing(true);
    setError('');
    setExtractedData(null);
    setStatusMessage('Connecting to Engineering Core...');

    try {
      setTimeout(() => setStatusMessage('Analyzing Site Logistics...'), 1000);
      setTimeout(() => setStatusMessage('Mapping Material Specifications...'), 2000);
      
      const data = await extractProjectFromText(text);
      if (data && data.name) {
        setExtractedData(data);
      } else {
        setError('Extraction failed: Could not identify project name or contractor.');
      }
    } catch (e) {
      console.error(e);
      setError('AI service error. Please check your network or API key configuration.');
    } finally {
      setIsAnalyzing(false);
      setStatusMessage('');
    }
  };

  const handleConfirm = () => {
    if (!extractedData) return;
    
    // Map extracted data to valid internal Project type
    // Critical: Generate IDs for bitumen specs which the AI returns as plain objects
    const bitumenSpecs: BitumenSpec[] = (extractedData.bitumenSpecs || []).map((spec: any) => ({
      id: crypto.randomUUID(),
      grade: spec.grade || 'Unknown',
      quantity: spec.quantity || 0
    }));

    const updates: ProjectUpdate[] = [{
      id: crypto.randomUUID(),
      date: new Date().toISOString().split('T')[0],
      content: 'Project record initialized via AI Text Import.',
      author: 'Nexus AI'
    }];

    // Fix: Added missing required country property from props
    const newProject: Project = {
      id: crypto.randomUUID(),
      name: extractedData.name || 'Imported Project',
      contractor: extractedData.contractor || 'Unknown',
      region: extractedData.region || 'Unspecified',
      country: activeCountry,
      kilometers: extractedData.kilometers || 0,
      financer: extractedData.financer || '',
      consultant: extractedData.consultant || '',
      status: (extractedData.status as any) || ProjectStatus.PLANNING,
      progress: extractedData.progress || 0,
      startDate: extractedData.startDate || new Date().toISOString().split('T')[0],
      estimatedCompletion: extractedData.estimatedCompletion || '',
      updates,
      tasks: [],
      bitumenSpecs,
      tenderNumber: extractedData.tenderNumber || ''
    };

    onImport(newProject);
    onClose();
    setText('');
    setExtractedData(null);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[150] overflow-y-auto bg-slate-900/60 flex items-center justify-center p-4 backdrop-blur-md">
      <div className="bg-white rounded-[40px] shadow-2xl w-full max-w-2xl flex flex-col max-h-[90vh] animate-in zoom-in-95 duration-300 border border-white/20">
        
        <header className="p-8 bg-indigo-600 text-white flex justify-between items-center rounded-t-[40px] relative overflow-hidden">
           <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 blur-3xl -mr-16 -mt-16"></div>
           <div className="flex items-center gap-4 relative z-10">
              <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center backdrop-blur-md">
                 <Sparkles size={24} />
              </div>
              <div>
                  <h2 className="text-xl font-black uppercase tracking-tight">AI Project Extraction</h2>
                  <p className="text-indigo-100 text-[10px] font-black uppercase tracking-widest mt-1">Nexus Infrastructure Intel</p>
              </div>
           </div>
           <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-xl transition-colors relative z-10">
            <X size={24} />
          </button>
        </header>

        <div className="p-8 overflow-y-auto space-y-6 custom-scrollbar">
            {!extractedData && !isAnalyzing ? (
                <div className="space-y-4 animate-in fade-in duration-300">
                    <div className="flex justify-between items-center">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-1">Raw Report Data</label>
                        <div className="flex gap-2">
                           {SAMPLE_TEXTS.map((sample, idx) => (
                             <button 
                                key={idx}
                                onClick={() => setText(sample.text)}
                                className="text-[10px] font-bold text-indigo-600 bg-indigo-50 px-3 py-1.5 rounded-lg hover:bg-indigo-100 transition-colors border border-indigo-100"
                             >
                               Try Sample {idx + 1}
                             </button>
                           ))}
                        </div>
                    </div>
                    <textarea 
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        placeholder="Paste road project briefs, email updates, or site reports here..."
                        className="w-full h-48 p-6 bg-slate-50 border border-slate-200 rounded-3xl focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 outline-none resize-none text-sm leading-relaxed transition-all shadow-inner"
                    />
                    
                    {error && (
                        <div className="p-4 bg-rose-50 border border-rose-100 text-rose-600 text-xs font-bold rounded-2xl flex items-center gap-3 animate-in slide-in-from-top-2">
                            <AlertCircle size={18} /> {error}
                        </div>
                    )}
                </div>
            ) : isAnalyzing ? (
                <div className="py-20 flex flex-col items-center justify-center space-y-6 animate-in fade-in">
                    <div className="relative">
                        <div className="w-24 h-24 border-4 border-indigo-100 border-t-indigo-600 rounded-full animate-spin"></div>
                        <Terminal className="absolute inset-0 m-auto text-indigo-500 animate-pulse" size={32} />
                    </div>
                    <div className="text-center">
                        <h4 className="text-lg font-black text-slate-800 tracking-tight">{statusMessage}</h4>
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.2em] mt-2">Gemini 3 Flash Processing</p>
                    </div>
                </div>
            ) : (
                <div className="space-y-6 animate-in slide-in-from-bottom-4 duration-500">
                    <div className="flex items-center justify-between">
                        <h3 className="text-sm font-black text-slate-800 uppercase tracking-widest flex items-center gap-2">
                           <ShieldCheck size={18} className="text-emerald-500" />
                           Identity Check: Standardized Output
                        </h3>
                        <button onClick={() => setExtractedData(null)} className="text-[10px] font-black text-indigo-600 uppercase hover:underline">Re-scan</button>
                    </div>
                    
                    <div className="bg-slate-50 border border-slate-200 rounded-[32px] p-8 grid grid-cols-1 md:grid-cols-2 gap-6 shadow-inner relative overflow-hidden">
                        <div className="md:col-span-2">
                            <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Project Registry Name</p>
                            <p className="font-black text-slate-900 text-xl leading-tight">{extractedData.name}</p>
                        </div>
                        
                        <div className="space-y-1">
                            <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Region</p>
                            <p className="font-bold text-slate-700">{extractedData.region}</p>
                        </div>
                        
                        <div className="space-y-1">
                            <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Distance</p>
                            <p className="font-black text-slate-900">{extractedData.kilometers} KM</p>
                        </div>

                        <div className="space-y-1">
                            <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Main Contractor</p>
                            <p className="font-bold text-indigo-600 uppercase tracking-tight">{extractedData.contractor}</p>
                        </div>

                        <div className="space-y-1">
                            <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Current Progress</p>
                             <div className="flex items-center gap-2">
                                <span className="text-sm font-black text-slate-800">{extractedData.progress}%</span>
                                <div className="w-16 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                                    <div className="bg-indigo-500 h-full rounded-full" style={{width: `${extractedData.progress}%`}}></div>
                                </div>
                             </div>
                        </div>
                        
                        {extractedData.bitumenSpecs && extractedData.bitumenSpecs.length > 0 && (
                            <div className="md:col-span-2 pt-6 border-t border-slate-200 mt-2">
                                <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-4 flex items-center gap-2">
                                    <Droplet size={12} className="text-indigo-500" />
                                    Material Specification Mapping
                                </p>
                                <div className="grid grid-cols-2 gap-3">
                                    {extractedData.bitumenSpecs.map((spec: any, i: number) => (
                                        <div key={i} className="bg-white px-4 py-3 rounded-2xl border border-slate-200 flex justify-between items-center shadow-sm">
                                            <span className="text-xs font-black text-slate-700 uppercase">{spec.grade}</span>
                                            <span className="text-xs font-black text-indigo-600">{spec.quantity} MT</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>

        <footer className="p-8 bg-slate-50 border-t border-slate-100 rounded-b-[40px] flex justify-end gap-3 shrink-0">
             <button
                onClick={onClose}
                className="px-8 py-3 text-slate-500 font-black text-[10px] uppercase tracking-widest hover:bg-slate-200 rounded-2xl transition-all"
              >
                Discard
              </button>
              
              {!extractedData && !isAnalyzing ? (
                  <button
                    onClick={handleAnalyze}
                    disabled={!text.trim() || isAnalyzing}
                    className="px-10 py-3 bg-indigo-600 text-white rounded-2xl font-black text-[10px] uppercase tracking-[0.2em] shadow-xl shadow-indigo-100 hover:bg-indigo-700 transition-all active:scale-95 disabled:opacity-50 flex items-center gap-3"
                  >
                    <Sparkles size={18} /> RUN EXTRACTION
                  </button>
              ) : extractedData && (
                   <button
                    onClick={handleConfirm}
                    className="px-10 py-3 bg-emerald-600 text-white rounded-2xl font-black text-[10px] uppercase tracking-[0.2em] shadow-xl shadow-emerald-100 hover:bg-emerald-700 transition-all active:scale-95 flex items-center gap-3"
                  >
                    <CheckCircle size={18} /> INITIALIZE PROJECT
                  </button>
              )}
        </footer>
      </div>
    </div>
  );
};
